/**
 * Wallets hooks with mock fallback. Reads fall back to the mock store when
 * anonymous or the API errors. The only mutation is the sanctioned top-up
 * (`POST /wallets/:id/topup`); there is no wallet create/delete endpoint.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_EXCHANGE_RATE, WALLET_ALERT_THRESHOLD_USD } from "@/lib/constants";
import { WALLETS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { Wallet } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { walletsApi } from "./wallets.api";
import { toWallet } from "./wallets.adapter";
import { useWalletsStore } from "./wallets.store";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UseWalletsResult {
  wallets: Wallet[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function useWallets(): UseWalletsResult {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockActive = useWalletsStore((s) => s.active);

  const query = useQuery({
    queryKey: queryKeys.walletsList(LIST_PARAMS),
    queryFn: () => walletsApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (!authed) return { wallets: mockActive, isLoading: false, isError: false, isFetching: false, source: "mock", error: null, refetch: query.refetch };
  if (query.data) return { wallets: query.data.data.map(toWallet), isLoading: false, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
  if (query.isError) return { wallets: mockActive, isLoading: false, isError: true, isFetching: false, source: "mock", error: query.error, refetch: query.refetch };
  return { wallets: [], isLoading: query.isLoading, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
}

/** No dedicated `/wallets/:id` endpoint — derive a single wallet from the list. */
export function useWallet(id: string | undefined): { wallet: Wallet | undefined; isLoading: boolean; isError: boolean; source: DataSource } {
  const { wallets, isLoading, isError, source } = useWallets();
  return { wallet: id ? wallets.find((w) => w.id === id) : undefined, isLoading, isError, source };
}

export interface UseWalletAlertsResult {
  alerts: Wallet[];
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
}

export function useWalletAlerts(): UseWalletAlertsResult {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockActive = useWalletsStore((s) => s.active);
  const mockAlerts = mockActive.filter((w) => w.balanceUsd < WALLET_ALERT_THRESHOLD_USD);

  const query = useQuery({
    queryKey: queryKeys.walletAlerts(LIST_PARAMS),
    queryFn: () => walletsApi.alerts(LIST_PARAMS),
    enabled: authed,
  });

  if (!authed) return { alerts: mockAlerts, isLoading: false, isError: false, source: "mock" };
  if (query.data) return { alerts: query.data.data.map(toWallet), isLoading: false, isError: false, source: "api" };
  if (query.isError) return { alerts: mockAlerts, isLoading: false, isError: true, source: "mock" };
  return { alerts: [], isLoading: query.isLoading, isError: false, source: "api" };
}

export function useTopUpWallet() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const topupMock = useWalletsStore((s) => s.topup);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ walletId, amountUsd }: { walletId: string; amountUsd: number }) => {
      if (authed) return walletsApi.topup(walletId, { amountUsd });
      topupMock(walletId, amountUsd, DEFAULT_EXCHANGE_RATE);
      return null;
    },
    onSuccess: () => {
      if (authed) {
        void qc.invalidateQueries({ queryKey: WALLETS_QUERY_PREFIX });
        void qc.invalidateQueries({ queryKey: ["wallet-transactions"] });
      }
    },
  });
}
